import { toast } from 'sonner';
import type { ActiveTimer, Mode, Settings, Task } from '@timato/shared';
import { timerDeviceId } from '@/auth/thisDevice';
import { saveRow } from '@/data/repo';
import { labelsForGoal, recomputeTrackedSec } from '@/data/tasks';
import { clockNow } from '@/lib/clock';
import { teamIdForProject } from '@/team/cache';
import { claimForStart, teamOfTask } from '@/team/locks';
import type { Outcome } from './completion';
import { isBreak } from './engine';

/**
 * What a transition needs to know besides the row: the clock, the settings, this device, and where the goal belongs.
 * The labels are the ones picked on the goal card for this session, or else the goal's as they are now.
 */
export const transitionCtx = async (t: ActiveTimer, settings: Settings) => ({
  now: clockNow(), settings, deviceId: timerDeviceId(), labelIds: t.labelIds ?? await labelsForGoal(t.taskId, t.projectId), teamId: await teamIdForProject(t.projectId),
});

/** Writes an outcome: the logged session, if any, then the row the timer moves on to. */
export const applyOutcome = async ({ session, next }: Outcome) => {
  if (session) { await saveRow('sessions', session); await recomputeTrackedSec(session.taskId); }
  await saveRow('activeTimer', next);
};

/**
 * A shared task is claimed before its timer starts (hard lock, spec §4.2). Refused online: says who holds it and
 * answers false, so nothing starts. Offline it starts optimistically and is reconciled later. Breaks claim nothing.
 */
export async function claimToStart(task: Task | null | undefined, mode: Mode): Promise<boolean> {
  if (!task || isBreak(mode)) return true;
  const teamId = await teamOfTask(task);
  if (!teamId) return true;
  const r = await claimForStart(task, teamId);
  if (!r.ok) toast('Task is locked', { description: `${r.holderName ?? 'A teammate'} is working on “${task.name}”. Pick another task or ask them to release it.` });
  return r.ok;
}
